import React, { useState } from 'react';
import {
  Card,
  Table,
  Form,
  Input,
  Button,
  Space,
  Select,
  message,
  Switch,
  Popconfirm,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  getNoticeDetail,
  getNoticeList,
  deleteNotice,
  publishNotice,
} from '@/api/voteActivity';
import type { VoteContestListParams, VoteContest } from '@/api/voteActivity';
import dayjs from 'dayjs';
import { useRequest } from 'ahooks';
import VoteActivityDrawer from './Drawer';
import { LINKTypeMap } from './type';

const name = '活动公告';
const VoteActivityPage: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<VoteContest>();
  const [searchParams, setSearchParams] = useState<VoteContestListParams>({
    currentPage: 1,
    pageSize: 10,
  });

  const {
    data: _data,
    run,
    loading,
    refresh,
  } = useRequest(async (params = {}) => {
    const currentPage = params.currentPage ?? 1;
    const pageSize = params.pageSize ?? 20;
    const res = await getNoticeList({
      currentPage,
      pageSize,
      ...form.getFieldsValue()
    });
    if (res.status === 200 && res.data.status === 200) {
      return res.data.data;
    }

    return {
      records: [],
      total: 0,
      currentPage,
      pageSize,
    };
  });

  const handleSearch = async () => {
    const values = form.getFieldsValue();
    setSearchParams({ ...searchParams, ...values });
    run();
  };

  const handleReset = () => {
    form.resetFields();
    setSearchParams({
      currentPage: 1,
      pageSize: 10,
    });
    run();
  };

  const handleCreate = () => {
    setEditingActivity(undefined);
    setIsDrawerOpen(true);
  };

  const handleEdit = async (record: VoteContest) => {
    const res = await getNoticeDetail({ id: record.id! });
    if (res.status !== 200 || res.data?.status !== 200) return;
    setEditingActivity(res.data.data);
    setIsDrawerOpen(true);
  };

  const onDelete = async (id: number) => {
    try {
      const res = await deleteNotice({ id });
      const data = res.data;
      if (data?.status === 200) {
        message.success('删除成功');

        if (_data?.records.length > 1) {
          refresh();
        } else {
          const currentPage = _data?.currentPage;
          if (currentPage > 1) {
            run({ currentPage: currentPage - 1 });
          } else {
            refresh();
          }
        }
      } else {
        message.error(res.message || '删除失败');
      }
    } catch (error) {
      console.error('删除失败:', error);
      message.error('删除失败');
    }
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const handleDrawerSuccess = () => {
    setIsDrawerOpen(false);
    setEditingActivity(undefined);
    refresh();
  };

  const handleViewSubmissions = (id: number, status: number) => {
    navigate(`/vote-activity/submissions/${id}?status=${status}`);
  };

  const onPublish = async (value: boolean, id: number) => {
    console.log(value)
    const res = await publishNotice({
      publishStatus: Number(value),
      id,
    });

    if (res.status === 200 && res.data?.status === 200) {
      message.success('发布成功');
      refresh();
      return;
    }

    message.error(res.data?.message);
  };

  const columns = [
    {
      title: '创建时间',
      dataIndex: 'createTime',
      render: (_: string) => dayjs(_).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '公告内容',
      key: 'content',
      dataIndex: 'content',
      width: 300,
    },
    {
      title: '跳转链接',
      dataIndex: 'linkType',
      key: 'linkType',
      render: (_) => LINKTypeMap.get(_),
    },
    {
      title: '操作人员',
      dataIndex: 'createUserName',
      key: 'createUserName',
    },
    {
      title: '是否发布',
      dataIndex: 'publishStatus',
      key: 'publishStatus',
      render: (_, record) => (
        <Popconfirm
          title={`发布${name}`}
          description={`你确定要${_ ? '取消发布' : '发布'}这个${name}吗?`}
          onConfirm={() => onPublish(!_, record.id!)}
          okText="确定"
          cancelText="取消"
        >
          <Switch value={_} />
        </Popconfirm>

      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record: VoteContest) => (
        <Space>
          <Button type="link" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title={`删除${name}`}
            description={`你确定要删除这个${name}吗?`}
            onConfirm={() => onDelete(record.id!)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={name}
      extra={
        <Button type="primary" onClick={handleCreate}>
          创建{name}
        </Button>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline">
          <Form.Item name="content" label="公告内容">
            <Input placeholder="请输入公告内容" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="linkType" label="跳转链接">
            <Select
              style={{ width: 160 }}
              placeholder="请选择跳转链接"
              allowClear
              options={[...LINKTypeMap.values()].map((item, index) => ({
                label: item,
                value: index,
              }))}
            />
          </Form.Item>
          <Form.Item name="link" label="链接ID">
            <Input placeholder="请输入链接ID" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" onClick={handleSearch}>
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </div>

      <Table
        columns={columns}
        dataSource={_data?.records}
        rowKey="id"
        pagination={{
          current: _data?.currentPage ?? 1,
          pageSize: _data?.pageSize ?? 10,
          total: _data?.total,
          showSizeChanger: true,
          showQuickJumper: true,
          onChange: (page, pageSize) => run({ currentPage: page, pageSize }),
        }}
        loading={loading}
        scroll={{ x: 1200 }}
      />

      {
        isDrawerOpen && (
          <VoteActivityDrawer
            name={name}
            data={editingActivity}
            onSuccess={handleDrawerSuccess}
            onClose={handleDrawerClose}
          />
        )
      }
    </Card>
  );
};

export default VoteActivityPage;
